/**
 * Utilitário para gerenciamento de popups com fallback para modais
 */

/**
 * Verifica se popups estão bloqueados no navegador
 * @returns {Promise<boolean>} true se popups estiverem bloqueados
 */
export const checkPopupBlocker = () => {
  return new Promise((resolve) => {
    try {
      // Tenta abrir uma janela popup de teste
      const testWindow = window.open('', '_blank', 'width=1,height=1,left=-1000,top=-1000');
      
      if (!testWindow || testWindow.closed || typeof testWindow.closed === 'undefined') {
        // Pop-up foi bloqueado
        resolve(true);
      } else {
        // Pop-up não foi bloqueado, fecha a janela de teste
        testWindow.close();
        resolve(false);
      }
    } catch (e) {
      // Se houver erro, assume que está bloqueado
      resolve(true);
    }
  });
};

/**
 * Abre um popup com configurações otimizadas
 * @param {string} url - URL para abrir no popup
 * @param {string} windowName - Nome da janela
 * @param {Object} options - Opções do popup
 * @param {number} options.width - Largura do popup
 * @param {number} options.height - Altura do popup
 * @param {boolean} options.center - Se deve centralizar na tela
 * @returns {Window|null} A janela aberta ou null se bloqueado
 */
export const openPopup = (url, windowName, options = {}) => {
  const {
    width = 900,
    height = 700,
    center = true,
    resizable = true,
    scrollbars = true,
    menubar = false,
    toolbar = false,
    location = false,
    status = false
  } = options;

  let features = `width=${width},height=${height}`;

  if (center) {
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    features += `,left=${left},top=${top}`;
  }

  features += `,resizable=${resizable ? 'yes' : 'no'}`;
  features += `,scrollbars=${scrollbars ? 'yes' : 'no'}`;
  features += `,menubar=${menubar ? 'yes' : 'no'}`;
  features += `,toolbar=${toolbar ? 'yes' : 'no'}`;
  features += `,location=${location ? 'yes' : 'no'}`;
  features += `,status=${status ? 'yes' : 'no'}`;

  try {
    const popupWindow = window.open(url, windowName, features);
    
    if (!popupWindow || popupWindow.closed) {
      console.warn('⚠️ Popup bloqueado ou falha ao abrir');
      return null;
    }

    // Foca na janela popup
    popupWindow.focus();
    
    // Monitora se a janela foi fechada
    const checkInterval = setInterval(() => {
      if (popupWindow.closed) {
        clearInterval(checkInterval);
        console.log('✅ Popup fechado pelo usuário');
      }
    }, 1000);

    return popupWindow;
  } catch (error) {
    console.error('❌ Erro ao abrir popup:', error);
    return null;
  }
};

/**
 * Abre um popup com fallback para modal interno
 * @param {string} url - URL para abrir
 * @param {string} windowName - Nome da janela
 * @param {Object} options - Opções do popup
 * @param {Function} onPopupBlocked - Callback quando popup é bloqueado
 * @returns {Object} { success: boolean, window: Window|null, fallbackUsed: boolean }
 */
export const openPopupWithFallback = async (url, windowName, options = {}, onPopupBlocked = null) => {
  // Primeiro verifica se popups estão bloqueados
  const isBlocked = await checkPopupBlocker();
  
  if (isBlocked) {
    console.warn('🚫 Popups bloqueados - usando fallback');
    
    if (onPopupBlocked && typeof onPopupBlocked === 'function') {
      onPopupBlocked();
    }
    
    // Retorna indicando que precisa de fallback
    return {
      success: false,
      window: null,
      fallbackUsed: true,
      reason: 'popup_blocked'
    };
  }
  
  // Tenta abrir o popup
  const popupWindow = openPopup(url, windowName, options);
  
  if (!popupWindow) {
    console.warn('⚠️ Falha ao abrir popup - usando fallback');
    
    if (onPopupBlocked && typeof onPopupBlocked === 'function') {
      onPopupBlocked();
    }
    
    return {
      success: false,
      window: null,
      fallbackUsed: true,
      reason: 'open_failed'
    };
  }
  
  return {
    success: true,
    window: popupWindow,
    fallbackUsed: false,
    reason: null
  };
};

/**
 * Utilitário específico para janelas de resultado PIX
 */
export const pixPopupUtils = {
  /**
   * Abre popup para resultado de link PIX aleatório
   * @param {Object} linkData - Dados do link PIX
   * @returns {Object} Resultado da operação
   */
  openResultadoAleatorio: async (linkData) => {
    // Salva dados no localStorage para a página de resultado
    if (linkData) {
      localStorage.setItem('linkPixAleatorio', JSON.stringify({
        ...linkData,
        timestamp: Date.now()
      }));
    }
    
    return await openPopupWithFallback(
      '/pix/resultado-aleatorio',
      'ResultadoPixAleatorio',
      { width: 900, height: 700 },
      () => {
        // Fallback: mostra alerta ou redireciona
        alert('⚠️ Popup bloqueado! Por favor, permita popups para este site ou clique em "Gerar Link PIX" novamente para ver os resultados na mesma janela.');
      }
    );
  },
  
  /**
   * Abre popup para resultado de cobrança PIX
   * @param {Object} cobrancaData - Dados da cobrança PIX
   * @returns {Object} Resultado da operação
   */
  openResultadoCobranca: async (cobrancaData) => {
    // Salva dados no localStorage para a página de resultado
    if (cobrancaData) {
      localStorage.setItem('linkPixCobranca', JSON.stringify({
        ...cobrancaData,
        timestamp: Date.now()
      }));
    }
    
    return await openPopupWithFallback(
      '/pix/resultado-cobranca',
      'ResultadoPixCobranca',
      { width: 900, height: 700 },
      () => {
        // Fallback: mostra alerta ou redireciona
        alert('⚠️ Popup bloqueado! Por favor, permita popups para este site ou clique em "Gerar Link PIX" novamente para ver os resultados na mesma janela.');
      }
    );
  },
  
  /**
   * Verifica se há dados de PIX no localStorage
   * @param {string} type - Tipo de dados ('aleatorio' ou 'cobranca')
   * @returns {Object|null} Dados do PIX ou null
   */
  getStoredPixData: (type = 'aleatorio') => {
    const key = type === 'aleatorio' ? 'linkPixAleatorio' : 'linkPixCobranca';
    const data = localStorage.getItem(key);
    
    if (!data) return null;
    
    try {
      const parsed = JSON.parse(data);
      
      // Verifica se os dados não são muito antigos (10 minutos)
      const dataAtual = Date.now();
      const dataGerado = parsed.timestamp || dataAtual;
      const diferencaMinutos = (dataAtual - dataGerado) / 1000 / 60;
      
      if (diferencaMinutos > 10) {
        console.warn(`⚠️ Dados PIX com ${diferencaMinutos.toFixed(1)} minutos - considerando expirados`);
        localStorage.removeItem(key);
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.error('❌ Erro ao parsear dados PIX:', error);
      localStorage.removeItem(key);
      return null;
    }
  },
  
  /**
   * Limpa dados de PIX do localStorage
   * @param {string} type - Tipo de dados ('aleatorio', 'cobranca' ou 'all')
   */
  clearStoredPixData: (type = 'all') => {
    if (type === 'all' || type === 'aleatorio') {
      localStorage.removeItem('linkPixAleatorio');
    }
    if (type === 'all' || type === 'cobranca') {
      localStorage.removeItem('linkPixCobranca');
    }
  }
};

export default {
  checkPopupBlocker,
  openPopup,
  openPopupWithFallback,
  pixPopupUtils
};